import _ from 'underscore';
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  useImageExperienceAdPreviewModel,
  AdPreviewSinglePlacementLayout,
  getPlacementListMetaDataByImageType,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

import { ImageExperienceImage } from '../model/image-experience-image';
import { getOnRenderPlacement } from './unified-placement-render';

export const UnifiedPreviewGeneral = (props) => {
  const {
    image: editingImage, // ImageExperienceImage
    adPreviewConfig,
    previewContainerClassName,
  } = props;

  // filter the placement config, only keep the placemen type corresponding to the editing image type
  const finalPlacementConfig = useMemo(() => {
    const editingPlacementTypes = _.chain(getPlacementListMetaDataByImageType([editingImage.type]))
      .map('placementType')
      .uniq()
      .value();
    const filteredConfig = _.chain(adPreviewConfig.placementConfig)
      .map(({ placementSetName, placementTypes }) => ({
        placementSetName,
        placementTypes: _.intersection(placementTypes, editingPlacementTypes),
      }))
      .filter(({ placementTypes }) => !_.isEmpty(placementTypes))
      // keep only one placement type for each placement set
      .map(({ placementSetName, placementTypes }) => ({ placementSetName, placementTypes: [_.first(placementTypes)] }))
      .value();
    return filteredConfig;
  }, [editingImage.type, adPreviewConfig]);

  const {
    multiImagePlacementSets: placementSets,
    onUpdateImageExperienceImageList,
  } = useImageExperienceAdPreviewModel(finalPlacementConfig);

  useEffect(() => {
    onUpdateImageExperienceImageList([[editingImage]]);
  }, [editingImage, onUpdateImageExperienceImageList]);

  const onRenderPlacement = useMemo(() => {
    const placementPreviewProps = _.extend({}, _.pick(
      props,
      'previewTexts',
      'logo',
      'hotspots',
      'i18n',
      'isMultimediaAd',
      'isMultiMediaAdsPhase2Enabled',
      'isAdCreatorEnabled',
      //
      // todo: check the props, are they required?
      'dismissText',
      'permissions'
    ));

    return getOnRenderPlacement(placementPreviewProps, previewContainerClassName);
  }, [previewContainerClassName, props]);

  const placementList = useMemo(() => {
    // should only have one placement set
    const list = _.chain(placementSets).map(placementSet => placementSet.placementList).flatten().value();
    return list;
  }, [placementSets]);

  const placementData = useMemo(() => _.find(placementList, ({ placementMeta }) => placementMeta !== undefined), [placementList]);

  return (
    <AdPreviewSinglePlacementLayout
      placementData={placementData}
      onRenderPlacement={onRenderPlacement}
      hasContainer={false}
    />
  );
};

UnifiedPreviewGeneral.propTypes = {
  adPreviewConfig: PropTypes.shape({
    placementConfig: PropTypes.arrayOf(PropTypes.shape({
      placementSetName: PropTypes.string,
      placementTypes: PropTypes.arrayOf(PropTypes.string),
    })),
  }),
  image: PropTypes.instanceOf(ImageExperienceImage).isRequired,
  previewContainerClassName: PropTypes.string,
};

UnifiedPreviewGeneral.defaultProps = {
  adPreviewConfig: {},
  previewContainerClassName: undefined,
};
